import { zodResolver } from '@hookform/resolvers/zod'
import axios from 'axios'
import { KeyRound } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { z } from 'zod'
import { authApi } from '@/api/auth'
import { GuestAuthShell } from '@/components/auth/GuestAuthShell'
import { Button } from '@/components/ui/Button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { useMinCopy } from '@/i18n/useUiLocale'

type RequestFormValues = { email: string }
type ConfirmFormValues = { newPassword: string; confirmPassword: string }

export function PasswordResetPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const isConfirmMode = Boolean(token)
  const t = useMinCopy({ preferGuest: true }).auth
  const [requestSuccess, setRequestSuccess] = useState<string | null>(null)

  const requestSchema = useMemo(
    () => z.object({ email: z.string().email(t.invalidEmail) }),
    [t.invalidEmail],
  )

  const confirmSchema = useMemo(
    () =>
      z
        .object({
          newPassword: z
            .string()
            .min(8, t.passwordMin)
            .max(128)
            .regex(/[A-Z]/, t.passwordUpper)
            .regex(/[0-9]/, t.passwordDigit),
          confirmPassword: z.string(),
        })
        .refine((data) => data.newPassword === data.confirmPassword, {
          message: t.passwordsMismatch,
          path: ['confirmPassword'],
        }),
    [t],
  )

  const requestForm = useForm<RequestFormValues>({
    resolver: zodResolver(requestSchema),
    defaultValues: { email: '' },
  })

  const confirmForm = useForm<ConfirmFormValues>({
    resolver: zodResolver(confirmSchema),
    defaultValues: {
      newPassword: '',
      confirmPassword: '',
    },
  })

  const handleRequestSubmit = async (data: RequestFormValues) => {
    requestForm.clearErrors('root')

    try {
      await authApi.requestPasswordReset(data.email)
      setRequestSuccess(t.resetSent)
      requestForm.reset({ email: '' })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 429) {
        requestForm.setError('root', { message: t.resetTooMany })
        return
      }
      requestForm.setError('root', { message: t.resetUnable })
    }
  }

  const handleConfirmSubmit = async (data: ConfirmFormValues) => {
    confirmForm.clearErrors('root')

    if (!token) {
      confirmForm.setError('root', { message: t.resetLinkInvalid })
      return
    }

    try {
      await authApi.confirmPasswordReset(token, data.newPassword)
      navigate('/login', {
        replace: true,
        state: { message: t.passwordResetSuccess },
      })
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        confirmForm.setError('root', { message: t.resetLinkInvalid })
        return
      }

      confirmForm.setError('root', { message: t.resetUnableConfirm })
    }
  }

  return (
    <GuestAuthShell>
      <Card className="relative w-full max-w-md border-white/60 bg-white/90 shadow-2xl backdrop-blur-sm">
        <CardHeader className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <KeyRound className="h-6 w-6" />
          </div>
          <CardTitle>{isConfirmMode ? t.chooseNewPasswordTitle : t.resetTitle}</CardTitle>
          <CardDescription>
            {isConfirmMode ? t.chooseNewPasswordDescription : t.resetDescription}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isConfirmMode ? (
            <form className="space-y-5" onSubmit={confirmForm.handleSubmit(handleConfirmSubmit)}>
              <div className="space-y-2">
                <Label htmlFor="newPassword">{t.newPassword}</Label>
                <Input
                  id="newPassword"
                  type="password"
                  autoComplete="new-password"
                  hasError={Boolean(confirmForm.formState.errors.newPassword)}
                  {...confirmForm.register('newPassword')}
                />
                {confirmForm.formState.errors.newPassword ? (
                  <p className="text-sm text-destructive">
                    {confirmForm.formState.errors.newPassword.message}
                  </p>
                ) : null}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t.confirmNewPassword}</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  hasError={Boolean(confirmForm.formState.errors.confirmPassword)}
                  {...confirmForm.register('confirmPassword')}
                />
                {confirmForm.formState.errors.confirmPassword ? (
                  <p className="text-sm text-destructive">
                    {confirmForm.formState.errors.confirmPassword.message}
                  </p>
                ) : null}
              </div>

              {confirmForm.formState.errors.root?.message ? (
                <p className="text-sm text-destructive">
                  {confirmForm.formState.errors.root.message}
                </p>
              ) : null}

              <Button type="submit" loading={confirmForm.formState.isSubmitting}>
                {t.saveNewPassword}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                {t.rememberedPassword}{' '}
                <Link className="font-medium text-primary hover:underline" to="/login">
                  {t.signInLink}
                </Link>
              </p>
            </form>
          ) : (
            <form className="space-y-5" onSubmit={requestForm.handleSubmit(handleRequestSubmit)}>
              {requestSuccess ? (
                <div className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {requestSuccess}
                </div>
              ) : null}

              <div className="space-y-2">
                <Label htmlFor="email">{t.email}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  hasError={Boolean(requestForm.formState.errors.email)}
                  {...requestForm.register('email')}
                />
                {requestForm.formState.errors.email ? (
                  <p className="text-sm text-destructive">{requestForm.formState.errors.email.message}</p>
                ) : null}
              </div>

              {requestForm.formState.errors.root?.message ? (
                <p className="text-sm text-destructive">{requestForm.formState.errors.root.message}</p>
              ) : null}

              <Button type="submit" loading={requestForm.formState.isSubmitting}>
                {t.sendResetLink}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                {t.backTo}{' '}
                <Link className="font-medium text-primary hover:underline" to="/login">
                  {t.signInLink}
                </Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </GuestAuthShell>
  )
}
